import { useEffect, useMemo, useState } from "react";
import { Plus, Search, Edit3, Trash2, Save, Loader2, X, Image as ImageIcon, Copy, ExternalLink, FileText, Video } from "lucide-react";
import AdminShell from "../components/AdminShell";
import { useI18n } from "../i18n/I18nContext";
import { api } from "../lib/nailApi";

const TOKEN_KEY = "nailEvent.adminToken";

const EMPTY = {
  title: "", url: "", type: "image", folder: "Hero Images",
  tags: "", alt_text: "", description: "", source: "",
};

const TYPE_ICON = { image: ImageIcon, video: Video, document: FileText, logo: ImageIcon, other: FileText };

export default function AdminMediaLibrary() {
  const { lang } = useI18n();
  const token = sessionStorage.getItem(TOKEN_KEY) || "";
  const L = (vi, en) => (lang === "en" ? en : vi);
  const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

  const [items, setItems] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("");
  const [type, setType] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2000); };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (folder) params.folder = folder;
      if (type) params.type = type;
      if (q) params.q = q;
      const r = await api.get("/admin/media", { headers, params });
      setItems(r.data.media || []);
      setFolders(r.data.folders || []);
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [folder, type]); // eslint-disable-line

  const save = async (payload, id) => {
    try {
      if (id) await api.put(`/admin/media/${id}`, payload, { headers });
      else await api.post("/admin/media", payload, { headers });
      setEditing(null);
      flash(L("Đã lưu", "Saved"));
      load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/media/${id}`, { headers });
      setConfirmDelete(null);
      flash(L("Đã xóa", "Deleted"));
      load();
    } catch (err) { flash(err?.response?.data?.detail || "Error"); }
  };

  const copyUrl = async (url) => {
    try { await navigator.clipboard.writeText(url); flash(L("Đã sao chép URL", "URL copied")); }
    catch { window.prompt("URL", url); }
  };

  return (
    <AdminShell
      testid="admin-media-library"
      subtitle={L("Thư Viện", "Library")}
      title={L("Thư Viện Media", "Media Library")}
      actions={
        <button type="button" onClick={() => setEditing({ mode: "create", data: { ...EMPTY, folder: folder || "Hero Images" } })} data-testid="media-lib-add-btn"
          className="inline-flex items-center gap-1.5 bg-espresso text-linen rounded-full px-4 py-2 text-sm font-medium hover:bg-espresso-hover">
          <Plus className="w-4 h-4" /> {L("Thêm Media", "Add Media")}
        </button>
      }
    >
      <div className="max-w-[1400px]">
        {toast && <div data-testid="media-lib-toast" className="mb-4 inline-block bg-[#E1EDE6] text-[#2D4A3E] rounded-xl px-4 py-2 text-sm font-medium">{toast}</div>}

        <div className="bg-white border border-espresso/10 rounded-3xl p-4 md:p-5 mb-6 grid md:grid-cols-4 gap-3">
          <div className="md:col-span-2 flex items-center gap-2 border border-espresso/10 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-espresso-muted" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder={L("Tìm tiêu đề / mô tả...", "Search title / description...")}
              data-testid="media-lib-search" className="flex-1 bg-transparent text-sm focus:outline-none" />
          </div>
          <select value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="media-lib-folder-filter"
            className="border border-espresso/10 rounded-xl px-3 py-2 text-sm bg-white">
            <option value="">{L("Tất cả thư mục", "All folders")}</option>
            {folders.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <select value={type} onChange={(e) => setType(e.target.value)} data-testid="media-lib-type-filter"
            className="border border-espresso/10 rounded-xl px-3 py-2 text-sm bg-white">
            <option value="">{L("Tất cả loại", "All types")}</option>
            <option value="image">Image</option>
            <option value="video">Video</option>
            <option value="document">Document</option>
            <option value="logo">Logo</option>
            <option value="other">Other</option>
          </select>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-espresso-muted" /></div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-dashed border-espresso/20 rounded-3xl p-12 text-center text-espresso-muted">
            {L("Chưa có media nào. Bấm “Thêm Media” để bắt đầu.", "No media yet. Click “Add Media” to begin.")}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((m) => {
              const Icon = TYPE_ICON[m.type] || ImageIcon;
              return (
                <div key={m.id} data-testid={`media-lib-card-${m.id}`} className="bg-white border border-espresso/10 rounded-3xl overflow-hidden">
                  <div className="aspect-video bg-linen-100 flex items-center justify-center">
                    {m.type === "image" || m.type === "logo" ? (
                      m.url ? <img src={m.url} alt={m.alt_text || m.title} className="w-full h-full object-contain" /> : <Icon className="w-6 h-6 text-espresso-muted" />
                    ) : (
                      <Icon className="w-8 h-8 text-espresso-muted" />
                    )}
                  </div>
                  <div className="p-4">
                    <div className="font-medium text-espresso truncate text-sm">{m.title}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] uppercase tracking-[0.18em] text-emerald2 font-semibold">{m.type}</span>
                      {m.folder && <span className="text-[10px] text-espresso-muted">· {m.folder}</span>}
                    </div>
                    <div className="mt-3 flex items-center gap-1">
                      <button type="button" onClick={() => copyUrl(m.url)} title={L("Sao chép URL", "Copy URL")} data-testid={`media-copy-${m.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso">
                        <Copy className="w-4 h-4" />
                      </button>
                      <a href={m.url} target="_blank" rel="noopener noreferrer" title={L("Mở", "Open")} data-testid={`media-open-${m.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso">
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button type="button" onClick={() => setEditing({ mode: "edit", data: { ...m, tags: (m.tags || []).join(", ") } })} title={L("Chỉnh sửa", "Edit")} data-testid={`media-edit-${m.id}`} className="p-1.5 rounded-lg hover:bg-linen-100 text-espresso-soft hover:text-espresso">
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => setConfirmDelete(m)} title={L("Xóa", "Delete")} data-testid={`media-delete-${m.id}`} className="p-1.5 rounded-lg hover:bg-[#F5DADA] text-espresso-soft hover:text-[#7A2E2E]">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <MediaEditDialog mode={editing.mode} initial={editing.data} folders={folders} onClose={() => setEditing(null)}
          onSave={(payload) => save(payload, editing.mode === "edit" ? editing.data.id : null)} L={L} />
      )}
      {confirmDelete && (
        <ConfirmDialog
          title={L("Xóa media?", "Delete media?")}
          body={L("Hành động này không thể hoàn tác.", "This cannot be undone.")}
          onCancel={() => setConfirmDelete(null)} onConfirm={() => remove(confirmDelete.id)} L={L}
        />
      )}
    </AdminShell>
  );
}

function MediaEditDialog({ mode, initial, folders, onClose, onSave, L }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [imgValid, setImgValid] = useState(true);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title?.trim() || !form.url?.trim()) return;
    setSaving(true);
    const tags = typeof form.tags === "string"
      ? form.tags.split(",").map((s) => s.trim()).filter(Boolean)
      : (form.tags || []);
    await onSave({
      title: form.title.trim(), url: form.url.trim(),
      type: form.type || "image", folder: form.folder || null,
      tags, alt_text: form.alt_text || null,
      description: form.description || null, source: form.source || null,
    });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-charcoal/40 backdrop-blur-sm flex items-end md:items-center justify-center px-3 md:px-6 py-4 md:py-10" data-testid="media-lib-dialog">
      <form onSubmit={submit} className="w-full max-w-3xl bg-white rounded-3xl shadow-[0_30px_80px_-30px_rgba(0,0,0,0.5)] max-h-[92vh] flex flex-col">
        <div className="px-6 md:px-8 py-5 border-b border-warmgray-200 flex items-center justify-between">
          <h3 className="font-serif text-2xl md:text-3xl text-charcoal">{mode === "edit" ? L("Chỉnh sửa Media", "Edit Media") : L("Thêm Media", "Add Media")}</h3>
          <button type="button" onClick={onClose} data-testid="media-lib-dialog-close" className="text-warmgray-500 hover:text-charcoal"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 md:px-8 py-6 overflow-y-auto grid md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{L("Xem trước", "Preview")}</span>
            <div className="aspect-video rounded-2xl bg-warmgray-100 overflow-hidden flex items-center justify-center border border-warmgray-200">
              {form.url && (form.type === "image" || form.type === "logo") && imgValid ? (
                <img src={form.url} alt="" onError={() => setImgValid(false)} className="w-full h-full object-contain" />
              ) : <ImageIcon className="w-6 h-6 text-warmgray-500" />}
            </div>
          </div>
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Input label={L("Tiêu đề *", "Title *")} value={form.title} onChange={(v) => set("title", v)} testId="media-title" required className="sm:col-span-2" />
            <Input label={L("URL *", "URL *")} value={form.url} onChange={(v) => { set("url", v); setImgValid(true); }} placeholder="https://..." testId="media-url" required className="sm:col-span-2" />
            <Select label={L("Loại", "Type")} value={form.type} onChange={(v) => set("type", v)} testId="media-type" options={[
              ["image", "Image"], ["video", "Video"], ["document", "Document"], ["logo", "Logo"], ["other", "Other"],
            ]} />
            <Select label={L("Thư mục", "Folder")} value={form.folder || ""} onChange={(v) => set("folder", v)} testId="media-folder" options={folders.map((f) => [f, f])} />
            <Input label={L("Tags (cách bằng dấu phẩy)", "Tags (comma separated)")} value={form.tags} onChange={(v) => set("tags", v)} testId="media-tags" className="sm:col-span-2" />
            <Input label="Alt text" value={form.alt_text} onChange={(v) => set("alt_text", v)} testId="media-alt" />
            <Input label={L("Nguồn", "Source")} value={form.source} onChange={(v) => set("source", v)} testId="media-source" />
            <Textarea label={L("Mô tả", "Description")} value={form.description} onChange={(v) => set("description", v)} rows={2} testId="media-description" className="sm:col-span-2" />
          </div>
        </div>
        <div className="px-6 md:px-8 py-4 border-t border-warmgray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal" data-testid="media-lib-dialog-cancel">{L("Hủy", "Cancel")}</button>
          <button type="submit" disabled={!form.title?.trim() || !form.url?.trim() || saving} data-testid="media-lib-dialog-save"
            className="inline-flex items-center gap-2 bg-espresso text-linen rounded-full px-5 py-2 text-sm font-medium hover:bg-espresso-hover disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {L("Lưu", "Save")}
          </button>
        </div>
      </form>
    </div>
  );
}

function ConfirmDialog({ title, body, onCancel, onConfirm, L }) {
  return (
    <div className="fixed inset-0 z-[80] bg-charcoal/40 backdrop-blur-sm flex items-center justify-center px-4">
      <div className="w-full max-w-sm bg-white rounded-3xl p-6" data-testid="confirm-dialog">
        <h3 className="font-serif text-2xl text-charcoal">{title}</h3>
        <p className="mt-2 text-sm text-warmgray-700">{body}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onCancel} data-testid="confirm-cancel" className="px-4 py-2 text-sm font-medium text-warmgray-700 hover:text-charcoal">{L("Hủy", "Cancel")}</button>
          <button type="button" onClick={onConfirm} data-testid="confirm-confirm" className="bg-[#7A2E2E] text-white rounded-full px-4 py-2 text-sm font-medium hover:opacity-90">{L("Xóa", "Delete")}</button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, placeholder, testId, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} required={required} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40" />
    </label>
  );
}
function Textarea({ label, value, onChange, rows = 3, testId, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40" />
    </label>
  );
}
function Select({ label, value, onChange, testId, options, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] font-semibold text-warmgray-700 mb-1.5">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full bg-white border border-warmgray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald2/40">
        <option value="">—</option>
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </label>
  );
}
